if (!customElements.get('cbm-complete-set')) {
  const formatINR = (paise) => {
    const rupees = paise / 100;
    const hasFraction = Math.round(rupees * 100) % 100 !== 0;
    return (
      '₹' +
      rupees.toLocaleString('en-IN', {
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: hasFraction ? 2 : 0,
      })
    );
  };

  customElements.define(
    'cbm-complete-set',
    class CompleteSet extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-cset-list]');
        this.button = this.querySelector('[data-cset-add]');
        this.buttonText = this.querySelector('[data-cset-btn-text]');
        this.error = this.querySelector('[data-cset-error]');

        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('click', this.onRowClick.bind(this));
        this.button.addEventListener('click', this.addAll.bind(this));

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onMainVariant(event));
        }

        if (this.dataset.url) {
          this.load();
        } else {
          this.refresh();
        }
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      async fetchRows(url) {
        if (!url) return [];
        try {
          const response = await fetch(url);
          if (!response.ok) return [];
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          return Array.from(doc.querySelectorAll('[data-cset-source] [data-cset-row]'));
        } catch (e) {
          return [];
        }
      }

      async load() {
        let rows = await this.fetchRows(this.dataset.url);
        if (!rows.length) rows = await this.fetchRows(this.dataset.fallbackUrl);
        if (!rows.length) {
          this.remove();
          return;
        }
        rows.forEach((row) => this.list.appendChild(document.importNode(row, true)));
        this.refresh();
        this.hidden = false;
      }

      onRowClick(event) {
        const row = event.target.closest('[data-cset-row]');
        if (!row || row.hasAttribute('data-cset-main')) return;
        if (event.target.closest('a, select, input, label, button')) return;
        const toggle = row.querySelector('[data-cset-toggle]');
        toggle.checked = !toggle.checked;
        toggle.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onChange(event) {
        const select = event.target.closest('[data-cset-variant]');
        if (select) {
          const option = select.selectedOptions[0];
          this.setRowVariant(select.closest('[data-cset-row]'), {
            id: option.value,
            price: Number(option.dataset.price),
            compare: Number(option.dataset.compare),
          });
        }
        this.refresh();
      }

      onMainVariant(event) {
        const variant = event && event.data && event.data.variant;
        const main = this.querySelector('[data-cset-main]');
        if (!variant || !main) return;
        const price = Number(variant.price);
        const compare = Math.max(Number(variant.compare_at_price) || price, price);
        this.setRowVariant(main, { id: variant.id, price, compare });
        const opt = main.querySelector('[data-cset-main-opt]');
        if (opt) opt.textContent = variant.title === 'Default Title' ? '' : variant.title;
        this.refresh();
      }

      setRowVariant(row, { id, price, compare }) {
        row.dataset.price = price;
        row.dataset.compare = compare;
        row.querySelector('[data-cset-id]').value = id;
        row.querySelector('[data-cset-now]').textContent = formatINR(price);
        const was = row.querySelector('[data-cset-was]');
        was.textContent = formatINR(compare);
        was.hidden = !(compare > price);
      }

      selectedRows() {
        return Array.from(this.list.querySelectorAll('[data-cset-row]')).filter(
          (row) => row.querySelector('[data-cset-toggle]').checked
        );
      }

      refresh() {
        const rows = this.list.querySelectorAll('[data-cset-row]');
        rows.forEach((row) => row.classList.toggle('is-off', !row.querySelector('[data-cset-toggle]').checked));

        const selected = this.selectedRows();
        const total = selected.reduce((sum, row) => sum + Number(row.dataset.price), 0);
        const totalWas = selected.reduce((sum, row) => sum + Number(row.dataset.compare), 0);
        const count = selected.length;

        this.querySelector('[data-cset-total]').textContent = formatINR(total);
        const was = this.querySelector('[data-cset-total-was]');
        was.textContent = formatINR(totalWas);
        was.hidden = !(totalWas > total);
        this.querySelector('[data-cset-count-label]').textContent =
          count === 1 ? 'Total for 1 item' : `Total for ${count} items`;

        this.buttonText.textContent = (this.dataset.buttonLabel || 'Add [count] to cart')
          .replace('[count]', count === 1 ? '1 item' : `all ${count}`)
          .replace(/\s+/g, ' ')
          .trim();
        this.button.disabled = count < 1;
        this.button.classList.remove('is-done');
        this.error.hidden = true;
      }

      async addAll() {
        const selected = this.selectedRows();
        if (selected.length < 1 || this.button.classList.contains('is-loading')) return;

        const formId = this.dataset.formId;
        const mainId = document.querySelector(`#${formId} input[name="id"]`)?.value;
        const mainQty = parseInt(document.querySelector(`input[name="quantity"][form="${formId}"]`)?.value, 10) || 1;

        const items = selected
          .map((row) => {
            const isMain = row.hasAttribute('data-cset-main');
            const id = isMain && mainId ? mainId : row.querySelector('[data-cset-id]').value;
            return { id: Number(id), quantity: isMain ? mainQty : 1 };
          })
          .filter((item) => item.id);

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const sections = cart && cart.getSectionsToRender ? cart.getSectionsToRender().map((s) => s.id) : [];

        this.button.classList.add('is-loading');
        this.button.setAttribute('aria-busy', 'true');
        this.error.hidden = true;

        let data;
        try {
          const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
          });
          data = await response.json();

          if (!response.ok || data.status) {
            this.showError(data.description || data.message);
            return;
          }
        } catch (e) {
          this.showError();
          return;
        } finally {
          this.button.classList.remove('is-loading');
          this.button.removeAttribute('aria-busy');
        }

        // The items are in the cart from here on, so a failing listener must never surface as an add error.
        this.button.classList.add('is-done');
        this.buttonText.textContent = this.dataset.addedLabel || 'Added to cart';

        try {
          if (cart && typeof cart.renderContents === 'function' && data.sections) {
            cart.classList.remove('is-empty');
            if (typeof cart.setActiveElement === 'function') cart.setActiveElement(this.button);
            cart.renderContents(data);
          } else {
            window.location = window.routes?.cart_url || '/cart';
          }
        } catch (e) {
          console.error(e);
          window.location = window.routes?.cart_url || '/cart';
        }

        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'complete-set', cartData: data })).catch(
              (e) => console.error(e)
            );
          }
        } catch (e) {
          console.error(e);
        }
      }

      showError(message) {
        this.error.textContent = message || 'Could not add these items. Please try again.';
        this.error.hidden = false;
      }
    }
  );
}
